/**
 * useEditModalState Hook
 *
 * Shared state management for edit modals.
 * Handles: date ranges, product loading, availabilities, initialization.
 */

import { useEffect, useState, useRef } from 'react';
import { isEqual } from 'lodash-es';
import { tzdate, TZDate } from '@repo/tzdate';
import { DateRange } from '@mui/x-date-pickers-pro';
import { CartItem } from '@repo/types';
import { useProduct, useAvailabilities } from '@repo/widget-utils/services/hooks/product';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { useBookingContext } from 'src/widgets/BookingContext';
import {
    setQuantities,
    setSelectedProducts,
    setSelectedTimeSlot,
    setSelectedTicketType,
    setHasChosenDate,
} from 'src/state/booking/bookingReducer';
import { formatTime } from '@repo/widget-utils/DateHelpers';
import { updateQuantityData } from '@repo/widget-utils/price-helper';
import useUpdateAvilabilitySearchPeriod from 'src/hooks/domain/cart/useUpdateAvilabilitySearchPeriod';
import useTicketTypesWithinProduct from 'src/hooks/domain/cart/useTicketTypesWithinProduct';
import usePriceQuantities from 'src/hooks/domain/cart/usePriceQuantities';
import useUpdateNewCartItem from 'src/hooks/domain/cart/useUpdateNewCartItem';
import { useWidgetEventEffect } from 'src/hooks/domain/events/useWidgetEventEffect';

// Constants
const HOURS_IN_DAY = 24;
const AVAILABILITY_LOOKBACK_MONTHS = 2;
const AVAILABILITY_LOOKAHEAD_MONTHS = 3;

export type UseEditModalStateOptions = {
    cartItem: CartItem;
    anchorEl: HTMLElement | null;
};

/**
 * Manages shared state for edit modals
 */
export function useEditModalState(options: UseEditModalStateOptions) {
    const { cartItem, anchorEl } = options;
    const config = useConfigurations();
    const { quantities, selectedProducts, bookingDispatch } = useBookingContext();

    const [dateRange, setDateRange] = useState<DateRange<TZDate>>([null, null]);

    // Extract first and last products for cleaner access
    const [firstProduct] = cartItem.products;
    const lastProduct = cartItem.products[cartItem.products.length - 1];

    // Determine product type for API calls
    const isTimeslots = firstProduct.product?.type === 'timeslot';
    const isAccommodation = firstProduct.product?.type === 'accommodation';

    // For accommodation, calculate total guests from ticket options
    // The API needs this to return correct pricing/availability
    const accommodationGuests = isAccommodation
        ? cartItem.ticketOptions.reduce((sum, opt) => sum + opt.quantity, 0)
        : undefined;

    // Load product data
    const { data: product } = useProduct(
        firstProduct.product?.id,
        isAccommodation ? 'accommodation' : isTimeslots ? 'timeslot' : undefined,
        [firstProduct.start, lastProduct.start],
        accommodationGuests,
    );

    // Availability period management
    const { nextProductAvailabilityStart, availabilitySearchPeriod, setAvailabilitySearchPeriod } =
        useUpdateAvilabilitySearchPeriod(product);

    // Load availabilities
    const { data: availabilities, isLoading: isLoadingAvailabilities } = useAvailabilities(
        product,
        availabilitySearchPeriod.startDay,
        availabilitySearchPeriod.endDay,
        config.includeClosedAvailability,
    );

    // Update payment plan context
    useUpdateNewCartItem(cartItem.disablePaymentPlans);

    // Get ticket types and price quantities
    const ticketTypes = useTicketTypesWithinProduct(product);
    const priceQuantities = usePriceQuantities(product, selectedProducts, quantities);

    // Update quantities when product/selectedProducts change
    // Use refs to avoid infinite loop from quantities dependency
    const prevProductRef = useRef(product);
    const prevSelectedProductsRef = useRef(selectedProducts);

    useEffect(() => {
        // Only run if product or selectedProducts actually changed
        if (
            prevProductRef.current === product &&
            prevSelectedProductsRef.current === selectedProducts
        ) {
            return;
        }

        const updatedQuantities = updateQuantityData(
            selectedProducts ?? [],
            product?.ticketOptions ?? [],
            quantities,
        );

        if (!isEqual(updatedQuantities, quantities)) {
            bookingDispatch(setQuantities(updatedQuantities));
        }

        prevProductRef.current = product;
        prevSelectedProductsRef.current = selectedProducts;
    }, [selectedProducts, product, quantities, bookingDispatch]);

    // Initialize modal state when opened
    useEffect(() => {
        if (!cartItem || !anchorEl) return;

        const productStartDate = tzdate(firstProduct.start);

        // Single product - set time slot
        if (cartItem.products.length < 2) {
            const productEndDate = tzdate(firstProduct.end);
            const durationHours = productEndDate.diff(productStartDate, 'hours');
            const shouldShowEndTime = durationHours < HOURS_IN_DAY;

            bookingDispatch(
                setSelectedTimeSlot({
                    product: firstProduct,
                    label: formatTime(productStartDate),
                    sublabel: shouldShowEndTime ? formatTime(productEndDate) : undefined,
                    products: firstProduct.timeslots ?? [],
                }),
            );
        } else {
            // Multiple products - set date range
            const productEndDate = tzdate(lastProduct.start);
            setDateRange([productStartDate, productEndDate]);
        }

        // Set selected state
        bookingDispatch(setSelectedProducts(cartItem.products));
        bookingDispatch(setSelectedTicketType(cartItem.ticketType));

        // Set availability search period
        setAvailabilitySearchPeriod({
            startDay: productStartDate
                .subtract(AVAILABILITY_LOOKBACK_MONTHS, 'month')
                .startOf('month'),
            endDay: productStartDate.add(AVAILABILITY_LOOKAHEAD_MONTHS, 'month').startOf('month'),
        });

        // Set quantities
        bookingDispatch(setQuantities(cartItem.ticketOptions));
    }, [
        cartItem,
        anchorEl,
        bookingDispatch,
        setAvailabilitySearchPeriod,
        firstProduct,
        lastProduct,
    ]);

    // Mark date as chosen
    useEffect(() => {
        bookingDispatch(setHasChosenDate(true));
    }, [bookingDispatch]);

    // Track view event
    // Only track if product exists (validates cart structure)
    const cartItemProduct = firstProduct.product;
    useWidgetEventEffect(
        (_cartItem) => ({
            eventType: 'viewItem',
            productType: firstProduct.product?.type ?? 'timepoint',
            product: firstProduct.product!,
        }),
        cartItem,
        anchorEl,
        // Break condition: don't track if product is missing
        () => !cartItemProduct,
    );

    // Date range handler (prevents extending in both directions)
    function noExtendSetDateRange(newRange: DateRange<TZDate>): void {
        // If start date changed, reset end date (immutable update)
        const updatedRange: DateRange<TZDate> =
            dateRange[0]?.format('YYYY-MM-DD') !== newRange[0]?.format('YYYY-MM-DD')
                ? [newRange[0], null]
                : newRange;

        setDateRange(updatedRange);
    }

    // Min booking date (god mode or next available)
    const minBookingDate = config.enableGodMode
        ? tzdate().add(-1, 'year')
        : nextProductAvailabilityStart;

    // Calculate product capacity
    const productCapacity = (selectedProducts ?? cartItem.products).reduce(
        (acc, cur) => Math.min(acc, cur.capacity),
        Number.MAX_VALUE,
    );

    return {
        // Product data
        product,
        priceQuantities,
        ticketTypes,
        productCapacity,

        // Availability data
        availabilities,
        isLoadingAvailabilities,
        availabilitySearchPeriod,
        setAvailabilitySearchPeriod,
        minBookingDate,

        // Date range
        dateRange,
        setDateRange: noExtendSetDateRange,
    };
}
